import React from 'react';
import { ResumableOnboarding } from './onboardingContinueLabel.utils';

/**
 * Loading the onboardings a customer can resume.
 *
 * Extracted from the onboarding screen so the one rule that broke can be tested: WHEN the request
 * fires, not just what it does with the answer.
 *
 * A 4xx is terminal FOR ITS REQUEST — the endpoint rejects a call carrying no project scope, and
 * that is the answer for a customer who has no project yet, so retrying it would only delay the
 * onboarding UI. But "no project scope" is a STATE THAT CAN END, not a verdict about the customer.
 * Keying the effect on the scope is what separates those two: the rejection stays final for the
 * call that got it, and a later scope fires a fresh call.
 *
 * Before that distinction existed, one early unscoped rejection was the final answer for the whole
 * lifetime of the page: a customer whose scope arrived a moment later never had their started
 * onboarding loaded, and the resume control had no domain to name.
 */
export function useResumableOnboardings<TApi, TOnboarding extends ResumableOnboarding = ResumableOnboarding>(
  api: TApi | undefined,
  projectUuid: string | undefined,
  // Generic over what a row carries, so the screen renders every fact its loader reads.
  load: (api: TApi) => Promise<TOnboarding[]>,
  // `unknown` here is the accurate type for a caught value rather than a loose one: TypeScript types
  // every `catch` binding that way, and narrowing it is the handler's business.
  onNonTerminalError?: (error: unknown) => void
): { onboardings: TOnboarding[]; loading: boolean } {
  const [onboardings, setOnboardings] = React.useState<TOnboarding[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    let active = true;

    if (!api) {
      setLoading(false);
      return () => {
        active = false;
      };
    }

    setLoading(true);
    void (async () => {
      try {
        const result = await load(api);
        if (active) {
          setOnboardings(result);
        }
      } catch (error) {
        // Not retried here: a rejection is this request's answer. The dependency on the scope below
        // is what gives a later, better-formed request its chance.
        onNonTerminalError?.(error);
        if (active) {
          setOnboardings([]);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, projectUuid]);

  return { onboardings, loading };
}
